import { useMutation, useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { FormField } from "../../components/ui/FormField";
import { Input } from "../../components/ui/Input";
import { PageHeader } from "../../components/ui/PageHeader";
import { Select } from "../../components/ui/Select";
import { ErrorState, LoadingState } from "../../components/ui/State";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import { companyQrApi } from "../../lib/api/qr";
import { type Role, usersApi } from "../../lib/api/users";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

const STEPS = ["settings", "qr", "admin", "done"] as const;
type Step = (typeof STEPS)[number];

const DISMISSED_KEY = "odblox.setup-banner-dismissed-v1";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function SetupWizard() {
  useLocale();
  const navigate = useNavigate();
  const { step } = useParams<{ step?: string }>();
  const current = (STEPS as readonly string[]).includes(step ?? "") ? (step as Step) : null;

  if (!current) {
    return <Navigate to="/setup/settings" replace />;
  }

  const index = STEPS.indexOf(current);
  const isLast = index === STEPS.length - 1;

  const goto = (next: Step) => navigate(`/setup/${next}`);

  const finishAndDismiss = () => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Storage disabled / quota; treat as transient dismissal only.
    }
    navigate("/dashboard");
  };

  return (
    <div>
      <PageHeader
        eyebrow={t("setup.eyebrow")}
        title={t(`setup.step.${current}.title`)}
        subtitle={t("setup.subtitle")}
        actions={
          <>
            <Link to="/dashboard" className="btn btn--secondary btn--sm">
              {t("setup.action.exit")}
            </Link>
            <button type="button" className="btn btn--ghost btn--sm" onClick={finishAndDismiss}>
              {t("setup.dismissForever")}
            </button>
          </>
        }
      />

      <div
        className="stepper"
        aria-label={t("setup.stepperAriaLabel")}
        style={{ marginBottom: 16 }}
      >
        {STEPS.map((s, i) => (
          <span
            key={s}
            className={i === index ? "stepper__pill stepper__pill--active" : "stepper__pill"}
            aria-current={i === index ? "step" : undefined}
          >
            {i + 1}. {t(`setup.step.${s}.title`)}
          </span>
        ))}
      </div>

      <Card>
        <p style={{ marginTop: 0, lineHeight: 1.6 }}>{t(`setup.step.${current}.body`)}</p>
        {current === "settings" && (
          <Link to="/config/settings?setup=settings">
            <Button variant="secondary">{t("setup.step.settings.cta")}</Button>
          </Link>
        )}
        {current === "qr" && <SetupQRSection />}
        {current === "admin" && <SetupAdminSection onCreated={() => goto("done")} />}
      </Card>

      <div className="row" style={{ marginTop: 16, gap: 8, justifyContent: "space-between" }}>
        <Button
          variant="ghost"
          onClick={() => (index > 0 ? goto(STEPS[index - 1]) : navigate("/dashboard"))}
        >
          {index > 0 ? t("setup.action.back") : t("setup.action.exit")}
        </Button>
        <div className="row" style={{ gap: 8 }}>
          {!isLast && (
            <Button variant="secondary" onClick={() => goto(STEPS[index + 1])}>
              {t("setup.action.skip")}
            </Button>
          )}
          {isLast ? (
            <Button onClick={finishAndDismiss}>{t("setup.action.finish")}</Button>
          ) : (
            <Button onClick={() => goto(STEPS[index + 1])}>{t("setup.action.next")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SetupQRSection() {
  const qr = useQuery({
    queryKey: ["company", "qr"],
    queryFn: () => companyQrApi.get(),
    staleTime: 60_000,
  });

  return (
    <div style={{ marginTop: 12 }}>
      {qr.isLoading && <LoadingState />}
      {qr.error && <ErrorState titleKey="states.errorTitle" message={translateError(qr.error)} />}
      {qr.data && (
        <div
          style={{
            display: "flex",
            gap: 24,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 16,
              borderRadius: 12,
              border: "1px solid var(--color-border, #e5e5e5)",
            }}
          >
            <QRCodeSVG
              value={JSON.stringify(qr.data.qr_payload)}
              size={192}
              includeMargin
              level="M"
            />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
              {t("setup.step.qr.codeLabel")}
            </div>
            <div
              className="mono"
              style={{
                fontSize: 12,
                wordBreak: "break-all",
                padding: 8,
                background: "var(--color-bg-subtle, rgba(15, 23, 42, 0.04))",
                borderRadius: 6,
                marginBottom: 12,
              }}
            >
              {qr.data.raw_code}
            </div>
            <Link to="/config/enrollment?setup=qr">
              <Button variant="secondary" size="sm">
                {t("setup.step.qr.cta")}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupAdminSection({ onCreated }: { onCreated: () => void }) {
  const { push } = useToast();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("platform_admin");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      usersApi.create({
        email,
        full_name: fullName || undefined,
        password,
        role,
        is_active: true,
      }),
    onSuccess: (user) => {
      push({
        tone: "success",
        title: t("setup.step.admin.createdTitle"),
        body: user.email,
      });
      setEmail("");
      setFullName("");
      setPassword("");
      onCreated();
    },
    onError: (err) => setError(translateError(err)),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || password.length < 12) {
      setError(t("setup.step.admin.validationError"));
      return;
    }
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="setup-admin-grid">
        <FormField label={t("users.editor.field.email")} required>
          {(id) => (
            <Input
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              required
            />
          )}
        </FormField>
        <FormField label={t("users.editor.field.fullName")}>
          {(id) => (
            <Input
              id={id}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="off"
            />
          )}
        </FormField>
        <FormField label={t("users.editor.field.role")} required>
          {(id) => (
            <Select id={id} value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="platform_admin">{t("users.roles.platform_admin")}</option>
              <option value="superadmin">{t("users.roles.superadmin")}</option>
            </Select>
          )}
        </FormField>
        <FormField
          label={t("users.editor.field.password")}
          hint={t("setup.step.admin.passwordHint")}
          required
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={12}
            />
          )}
        </FormField>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <Button type="submit" loading={mutation.isPending}>
          {t("setup.step.admin.submit")}
        </Button>
        <Link to="/config/users?setup=admin">
          <Button type="button" variant="secondary">
            {t("setup.step.admin.cta")}
          </Button>
        </Link>
      </div>
    </form>
  );
}

export function isSetupBannerDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissSetupBanner(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    // ignore
  }
}
